// areaOpsSubmit — the submit + poll layer for the "Alan İşlemleri" pin/queue
// panel. This is the same job flow ImageAnimatorModal uses (POST to a MW_API
// endpoint → { jobId } → poll api/image-editing/job/{jobId} every 4s until a
// terminal status), narrowed to the 4 area-selection features and returning a
// plain result-image URL instead of driving modal state.
//
// Kept as a pure module (no React) so useAreaOpsQueue and unit tests can call
// it directly.

import { getToken } from "../../../../common/environment_config";
import { extractEditedImageUrls } from "../ImageAnimatorModal";

// The features that live in the pin panel.
//   kind "region" — the user drags a rectangle over the image (sent as
//     targetRegion). remove-text / translate still default to whole-image
//     processing server-side (mode:"all"), but the drawn region is sent as a
//     hint and gives a consistent "select an area" UX + a visible selection.
//     `wholeImage` ones can also skip the drawing ("Tüm görsele uygula"): the
//     pin then carries `whole: true`, no region, and posts what the AI Wizard's
//     whole-image form posted (`wholeEndpoint` when that was another route).
//   kind "whole" — moved here from the AI Wizard drawer; nothing to draw, the
//     card opens the popup straight away. `defaultConfig` seeds the pickers.
export const AREA_OP_FEATURES = [
  {
    id: "replace-product",
    name: "Replace Product",
    // Card only (the full name is cut off there); the popup keeps `name`.
    cardName: "AreaOps.ReplaceProductCard",
    endpoint: "api/image-editing/replace-product",
    kind: "region",
    needsReference: true,
  },
  {
    id: "remove-object",
    name: "Remove Object",
    endpoint: "api/image-editing/remove-object",
    kind: "region",
    wholeImage: true,
    // Without a region the prompt is the only thing naming the object.
    wholeNeedsPrompt: true,
  },
  {
    id: "remove-text",
    name: "Remove Text",
    endpoint: "api/image-editing/remove-text",
    kind: "region",
    wholeImage: true,
  },
  {
    id: "translate",
    name: "Translate Text",
    endpoint: "api/image-editing/translate-image-text",
    kind: "region",
    needsLanguage: true,
    wholeImage: true,
  },
  {
    id: "change-image-text",
    name: "Change Image Text",
    endpoint: "api/image-editing/change-image-text",
    kind: "region",
    needsTextChange: true,
    wholeImage: true,
  },
  {
    id: "edit-region",
    name: "Edit Region",
    endpoint: "api/image-editing/edit-region",
    kind: "region",
    needsPrompt: true,
    wholeImage: true,
    // Whole image = the wizard's "Görseli Düzenle" (same body, no region).
    wholeEndpoint: "api/image-editing/generate-image-from-image",
  },
  {
    id: "add-object",
    name: "Add Object",
    endpoint: "api/image-editing/add-object",
    kind: "region",
    needsPrompt: true,
  },
  {
    id: "add-logo",
    name: "Add Logo",
    endpoint: "api/image-editing/add-logo",
    kind: "region",
    needsReference: true,
  },
  {
    id: "remove-background",
    name: "Remove Background",
    endpoint: "api/image-editing/remove-background",
    kind: "whole",
  },
  {
    id: "upscale",
    name: "Upscale Image",
    endpoint: "api/image-editing/upscale-image",
    kind: "whole",
    defaultConfig: { scaleFactor: 2 },
  },
  {
    id: "resize",
    // Short label: the full "Görseli Yeniden Boyutlandır" is cut off on the card.
    name: "AreaOps.ResizeImage",
    endpoint: "api/image-editing/aspect-change",
    kind: "whole",
    defaultConfig: { aspectRatio: "16:9" },
  },
  {
    id: "generate-from-reference",
    name: "AreaOps.SimilarInfographic",
    endpoint: "api/image-editing/generate-from-reference",
    kind: "whole",
    needsPrompt: true,
  },
];

// AI Wizard features this modal now covers — EditorApp drops them from the
// wizard drawer (the catalogue itself stays: Görsel Ekle + AI chat use it).
// generate-image-from-image is edit-region's whole-image mode.
export const AREA_OPS_COVERED_WIZARD_IDS = new Set([
  "generate-image-from-image",
  "change-image-text",
  "replace-product",
  "remove-background",
  "remove-object",
  "remove-text",
  "translate",
  "resize",
  "upscale",
  "generate-from-reference",
]);

export function getAreaOpFeature(id) {
  return AREA_OP_FEATURES.find((f) => f.id === id) || null;
}

// A draft pin is ready to run only when its feature's required config is filled.
// (Matches the "disable the button, no warning alert" validation rule.)
// `whole`: the pin runs on the whole image (no region drawn).
export function isAreaOpReady(feature, config, { whole = false } = {}) {
  if (!feature) return false;
  if (whole && feature.wholeNeedsPrompt && !(config?.prompt || "").trim()) return false;
  if (feature.needsReference && !config?.referenceImageUrl) return false;
  if (feature.needsLanguage && !config?.targetLanguage) return false;
  if (
    feature.needsTextChange &&
    (!(config?.originalText || "").trim() || !(config?.newText || "").trim())
  )
    return false;
  if (feature.needsPrompt && !(config?.prompt || "").trim()) return false;
  return true;
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      if (signal.aborted) {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      signal.addEventListener(
        "abort",
        () => {
          clearTimeout(t);
          reject(new DOMException("Aborted", "AbortError"));
        },
        { once: true }
      );
    }
  });
}

// Build the request body for one area op. `region` is a normalized
// { x, y, width, height } (0-1) or null. `config` carries feature-specific
// inputs (referenceImageUrl / targetObject / prompt / adaptStyle / targetLanguage).
export function buildAreaOpBody({ feature, imageUrl, region, config, ctx }) {
  const { trainingId, navId, boxId, sceneIndex } = ctx || {};
  const body = {
    imageUrl,
    idealStudioTrainingId: trainingId,
    navId,
    boxId,
    sceneIndex,
  };

  if (feature.id === "replace-product") {
    body.referenceImageUrl = config?.referenceImageUrl || "";
    body.targetObject = config?.targetObject || "";
    body.prompt = config?.prompt || "";
    body.adaptStyle = config?.adaptStyle ?? false;
    if (region) body.targetRegion = region;
  } else if (feature.id === "remove-object") {
    body.prompt = config?.prompt || "";
    if (region) body.targetRegion = region;
  } else if (feature.id === "remove-text") {
    // mode:"all" = fal.ai text-removal, but the drawn region is now HONORED
    // server-side: when targetRegion is present the erase is confined to that
    // rectangle (crop → text-removal → composite back), so only the selected
    // area's text is removed instead of every text on the page.
    body.mode = "all";
    if (region) body.targetRegion = region;
  } else if (feature.id === "translate") {
    // mode:"all" = whole-image translate, but the drawn region is now HONORED
    // server-side: when targetRegion is present the translation is confined to
    // that rectangle (crop → OCR/translate/redraw → composite back), so only the
    // selected area's text is translated instead of every text on the page.
    body.targetLanguage = config?.targetLanguage || "";
    body.mode = "all";
    if (region) body.targetRegion = region;
  } else if (feature.id === "change-image-text") {
    body.textChanges = [
      {
        originalText: config?.originalText || "",
        newText: config?.newText || "",
        location: "",
      },
    ];
    if (region) body.targetRegion = region;
  } else if (feature.id === "edit-region") {
    // "Prompt ile Düzelt": edit only the drawn rectangle per the prompt. Region
    // is sent as a normalized rect (Gemini describes it as a % area server-side,
    // same convention as remove-object) and the prompt drives the change.
    body.prompt = config?.prompt || "";
    if (region) body.targetRegion = region;
  } else if (feature.id === "add-object") {
    // "Obje Ekle": Gemini inserts the described object into the drawn rectangle.
    body.prompt = config?.prompt || "";
    // The whole image always goes as the style reference (server-side).
    if (region) body.targetRegion = region;
  } else if (feature.id === "add-logo") {
    // "Logo Ekle": the uploaded logo (stored by the reference-upload UI as
    // config.referenceImageUrl) is composited into the drawn rectangle. Map it
    // to body.logoUrl for the add-logo endpoint. The SAME body also feeds the
    // training-wide endpoint when applyToAllImages is checked (it reads only
    // idealStudioTrainingId + logoUrl + targetRegion; extra fields are ignored).
    body.logoUrl = config?.referenceImageUrl || "";
    if (region) body.targetRegion = region;
  } else if (feature.id === "upscale") {
    // Whole-image ops below: the same bodies ImageAnimatorModal.handleSubmit sends.
    body.scaleFactor = config?.scaleFactor || 2;
  } else if (feature.id === "resize") {
    // The outpaint prompt per ratio is built server-side.
    body.aspectRatio = config?.aspectRatio || "16:9";
  } else if (feature.id === "generate-from-reference") {
    body.prompt = config?.prompt || "";
    body.count = 1;
    body.provider = "gemini-nano";
    // The scene's narration, fetched by the modal when the draft opens.
    body.narration = config?.narration || "";
  }
  // remove-background: no extra input.

  return body;
}

// Pick the endpoint for an area op. add-logo has a second mode: when the user
// checks "tüm görsellere uygula", it goes to the training-wide bulk endpoint
// (stamps the logo on every static image + saves exportedState) instead of the
// single-image add-logo. A whole-image pin of a feature with a `wholeEndpoint`
// goes there. Every other feature always uses its own endpoint.
export function resolveAreaOpEndpoint(feature, config, { whole = false } = {}) {
  if (feature?.id === "add-logo" && config?.applyToAllImages) {
    return "api/image-editing/apply-logo-to-training";
  }
  if (whole && feature?.wholeEndpoint) return feature.wholeEndpoint;
  return feature.endpoint;
}

// The bulk logo job rewrites many images and returns oldUrl→newUrl in
// result.urlMap (no single result.url). For the pin/queue the "result" of the
// op is the CURRENTLY edited image's new logoed URL; if that image wasn't a
// stamped target, nothing about it changed, so keep the input url.
export function resolveBulkLogoResultUrl(jobData, imageUrl) {
  const urlMap = jobData?.result?.urlMap || {};
  return urlMap[imageUrl] || imageUrl;
}

// POST the job. Returns the jobId. Throws on HTTP error / missing jobId.
export async function submitAreaOp({ feature, imageUrl, region, config, ctx, signal, whole }) {
  const apiUrl = process.env.MW_API_URL;
  if (!apiUrl) throw new Error("API URL not configured");

  const body = buildAreaOpBody({ feature, imageUrl, region, config, ctx });
  const endpoint = resolveAreaOpEndpoint(feature, config, { whole });

  const res = await fetch(`${apiUrl}${endpoint}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData?.message || errData?.error?.message || "Job submit failed");
  }

  const json = await res.json().catch(() => ({}));
  if (!json?.jobId) throw new Error("No jobId returned");
  return json.jobId;
}

// Poll a job to completion. Resolves with the result image URL, rejects on
// failure / 404 / timeout / abort. Mirrors ImageAnimatorModal.checkJobStatus'
// terminal handling + result-url extraction. maxAttempts × 4s is the timeout
// (batch jobs pass a longer one).
export async function pollAreaOpJob(
  jobId,
  { signal, onProgress, resolveResult, maxAttempts = 150 } = {}
) {
  const apiUrl = process.env.MW_API_URL;
  if (!apiUrl) throw new Error("API URL not configured");

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const res = await fetch(`${apiUrl}api/image-editing/job/${jobId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      signal,
    });

    if (res.status === 404) throw new Error("Job not found");
    if (!res.ok) throw new Error("Failed to check job status");

    const jobData = await res.json();
    const status = jobData?.status || jobData?.state || "unknown";
    let progress = jobData?.progress || 0;
    if (status === "processing" && progress === 0) progress = 50;
    else if (status === "completed") progress = 100;
    onProgress?.(progress, status);

    if (status === "completed") {
      // Bulk ops (training-wide logo) have no single result.url — the caller
      // supplies resolveResult to pull the relevant URL out of result.urlMap.
      if (resolveResult) return resolveResult(jobData);
      const base = jobData.result && typeof jobData.result === "object" ? jobData.result : null;
      const urls = extractEditedImageUrls(jobData);
      const url = (base && base.url) || urls[0] || null;
      if (!url) throw new Error("No result image URL");
      return url;
    }

    if (status === "failed" || status === "cancelled") {
      throw new Error(jobData.error || jobData.message || "Job failed");
    }

    await delay(4000, signal);
  }

  throw new Error("Job timed out");
}

// Submit + poll in one shot. Convenience used by the queue runner.
export async function runAreaOp({
  feature,
  imageUrl,
  region,
  config,
  ctx,
  signal,
  onProgress,
  onJobId,
  whole = false,
}) {
  const jobId = await submitAreaOp({ feature, imageUrl, region, config, ctx, signal, whole });
  onJobId?.(jobId);
  // add-logo "tüm görsellere uygula": the job stamps every image and returns a
  // urlMap. The pin/queue only needs THIS image's new URL — resolveResult pulls
  // it out (or keeps the input url if this image wasn't a target). Everything
  // else is already rewritten server-side in exportedState.
  const bulkLogo = feature.id === "add-logo" && !!config?.applyToAllImages;
  let urlMap = null;
  const resultUrl = await pollAreaOpJob(jobId, {
    signal,
    onProgress,
    resolveResult: bulkLogo
      ? (jobData) => {
          // Capture the full oldUrl→newUrl map so the caller can swap EVERY
          // affected image on the canvas, not just this one.
          urlMap = jobData?.result?.urlMap || {};
          return resolveBulkLogoResultUrl(jobData, imageUrl);
        }
      : undefined,
  });
  return { jobId, resultUrl, urlMap };
}

// Upload a reference image (replace-product). Reuses ImageAnimatorModal's
// endpoint: POST multipart "image" → { url }.
export async function uploadReferenceImage(file, { signal } = {}) {
  const apiUrl = process.env.MW_API_URL;
  if (!apiUrl) throw new Error("API URL not configured");
  const formData = new FormData();
  formData.append("image", file);
  const res = await fetch(`${apiUrl}api/image-editing/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
    signal,
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData?.message || "Failed to upload image");
  }
  const data = await res.json().catch(() => ({}));
  if (!data?.url) throw new Error("Failed to upload image");
  return data.url;
}
